import { useEffect, useState } from "react";
import { faShieldHalved } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { PanelEmptyState } from "@/components/mission-control/PanelEmptyState";
import { AuditCard } from "@/components/guardians/audits/AuditCard";
import { AuditInspector } from "@/components/guardians/audits/AuditInspector";
import { useScopeLabelCache } from "@/components/guardians/audits/useScopeLabelCache";
import { OpenInGuardiansButton } from "@/components/guardians/shared/OpenInGuardiansButton";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { Audit } from "@/services/api/guardiansv2/types";

export interface GuardiansTabProps {
  missionId: string;
}

/** Audits scoped to this mission. */
export function GuardiansTab({ missionId }: GuardiansTabProps) {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const scopeLabels = useScopeLabelCache(audits);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    GuardiansV2API.listAuditsByMission(missionId, { limit: 100 })
      .then((r) => {
        if (mounted) setAudits(r.audits);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [missionId]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedAudit(null);
      return;
    }
    const local = audits.find((a) => a._id === selectedId);
    if (local) {
      setSelectedAudit(local);
      return;
    }
    GuardiansV2API.getAudit(selectedId).then(setSelectedAudit).catch(() => {});
  }, [selectedId, audits]);

  const openInGuardians = (
    <OpenInGuardiansButton missionId={missionId} className="mt-3" />
  );

  if (loading) {
    return <p className="text-sm text-slate-400">Loading audits…</p>;
  }

  if (audits.length === 0) {
    return (
      <div className="flex min-h-[360px] flex-col" data-testid="mission-guardians-tab">
        <PanelEmptyState
          icon={faShieldHalved}
          title="No audits"
          subtitle="Guardians have not evaluated this mission yet."
          testId="mission-guardians-empty"
        />
        <div className="mt-auto border-t border-white/5 pt-3">{openInGuardians}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[360px] flex-col gap-4" data-testid="mission-guardians-tab">
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="min-w-0 flex-1 space-y-2 overflow-y-auto">
          {audits.map((audit) => (
            <AuditCard
              key={audit._id}
              audit={audit}
              selected={selectedId === audit._id}
              onClick={() => setSelectedId(audit._id)}
            />
          ))}
        </div>
        {selectedId ? (
          <aside className="flex min-w-0 flex-[1.2] overflow-hidden rounded-xl bg-slate-800">
            <AuditInspector
              audit={selectedAudit}
              loading={!selectedAudit}
              onClose={() => setSelectedId(null)}
              scopeLabels={scopeLabels}
            />
          </aside>
        ) : null}
      </div>
      <div className="shrink-0 border-t border-white/5 pt-3">{openInGuardians}</div>
    </div>
  );
}
